import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import StaticPageShell, { StaticCtaCard, StaticSection } from '../components/StaticPageShell';

/**
 * Guest marketing story for "How does it work?" - three calm steps.
 * Deep policy answers stay on FAQ / Shipping / Rules.
 */
const HowItWorks = () => {
    const { t } = useTranslation();
    const tr = (key: string, fallback: string) => translateWithFallback(t, key, fallback);

    const steps = [
        {
            icon: '🛒',
            titleKey: 'page.howItWorks.buy.title',
            titleFb: 'Buy safely',
            textKey: 'page.howItWorks.buy.text',
            textFb:
                'Browse pre-loved baby gear from Belgian parents. Pay securely at checkout with Bancontact, card, or PayPal. Shipping and buyer protection are calculated when you check out. After delivery, report a problem via your order or the helpdesk if something is wrong.',
        },
        {
            icon: '📦',
            titleKey: 'page.howItWorks.sell.title',
            titleFb: 'Sell simply',
            textKey: 'page.howItWorks.sell.text',
            textFb:
                'Create a free account, list clear photos and an honest condition, then ship when it sells. Buyers pay a small buyer protection fee at checkout; you keep the list price of your item.',
        },
        {
            icon: '🌱',
            titleKey: 'page.howItWorks.journey.title',
            titleFb: 'Grow with My Journey',
            textKey: 'page.howItWorks.journey.text',
            textFb:
                "Add your child on My Journey to track growth phases, checklist items, and product suggestions that fit their age - so the marketplace grows with your family.",
        },
        {
            icon: '📏',
            titleKey: 'page.howItWorks.petito.title',
            titleFb: 'My Petito is optional',
            textKey: 'page.howItWorks.petito.text',
            textFb:
                'The shop works without a child profile. Add one when you want checklists, size-aware suggestions, and outgrown tips. We store a nickname, birth or due date, and optional height/weight — not a health record. You can delete it anytime.',
        },
    ];

    return (
        <StaticPageShell
            eyebrow={tr('page.howItWorks.eyebrow', 'Getting started')}
            title={tr('page.howItWorks.title', 'How Petito works')}
            subtitle={tr(
                'page.howItWorks.subtitle',
                'Three simple steps for Belgian parents buying and selling pre-loved baby gear.'
            )}
            maxWidth="4xl"
        >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
                {steps.map((s, i) => (
                    <StaticSection
                        key={s.titleKey}
                        icon={s.icon}
                        title={`${i + 1}. ${tr(s.titleKey, s.titleFb)}`}
                    >
                        <p>{tr(s.textKey, s.textFb)}</p>
                    </StaticSection>
                ))}
            </div>

            <StaticCtaCard>
                <p className="mb-4 font-semibold sm:text-lg">
                    {tr('page.howItWorks.cta.title', 'Ready when you are')}
                </p>
                <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                    <Link
                        to="/register"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto"
                    >
                        {tr('welcome.cta.create', 'Create free account')}
                    </Link>
                    <Link
                        to="/home"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-[var(--accent-color)] bg-white/60 px-6 py-2.5 text-sm font-semibold text-[var(--text-color)] transition hover:bg-white/90 sm:w-auto"
                    >
                        {tr('welcome.browseWithout', 'Browse without account')}
                    </Link>
                    <Link
                        to="/faq"
                        className="inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--accent-color)] underline underline-offset-2 sm:w-auto"
                    >
                        {tr('page.howItWorks.faq.link', 'More in the FAQ')}
                    </Link>
                </div>
            </StaticCtaCard>
        </StaticPageShell>
    );
};

export default HowItWorks;
